import * as React from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { DayPicker } from "react-day-picker"

import { cn } from "../../lib/utils"
import { buttonVariants } from "./button"

export type CalendarProps = React.ComponentProps<typeof DayPicker>

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className)}
      classNames={{
        months: "flex flex-col sm:flex-row gap-2",
        month: "flex flex-col gap-4",
        month_caption: "flex justify-center items-center h-8 relative text-[#0B1F16]",
        dropdowns: "flex items-center gap-1",
        dropdown_root: "relative border rounded-md",
        dropdown: "absolute inset-0 z-10 opacity-0 cursor-pointer",
        caption_label:
          "flex items-center gap-1 px-2 py-1 text-sm font-medium pointer-events-none",
        nav: "flex items-center justify-between absolute inset-x-1 top-1 z-10",
        button_previous: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent p-0 text-gray-500 hover:text-[#0B1F16] hover:bg-gray-100"
        ),
        button_next: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent p-0 text-gray-500 hover:text-[#0B1F16] hover:bg-gray-100"
        ),
        month_grid: "w-full border-collapse",
        weekdays: "flex",
        weekday: "text-gray-500 w-8 font-normal text-[0.8rem]",
        week: "flex w-full mt-2",
        day: "relative h-8 w-8 p-0 text-center text-sm focus-within:relative focus-within:z-20",
        day_button: cn(
          buttonVariants({ variant: "ghost" }),
          "h-8 w-8 p-0 font-normal text-[#0B1F16] hover:bg-gray-100 aria-selected:opacity-100"
        ),
        selected:
          "[&>button]:bg-[#00553E] [&>button]:text-white [&>button]:hover:bg-[#00553E] [&>button]:hover:text-white [&>button]:focus:bg-[#00553E] [&>button]:focus:text-white",
        today: "[&>button]:border [&>button]:border-[#00553E]",
        outside: "text-gray-400 opacity-60",
        disabled: "text-gray-300 opacity-50 hover:bg-transparent",
        hidden: "invisible",
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation, className: chevronClassName, ...chevronProps }) =>
          orientation === "left" ? (
            <ChevronLeft className={cn("h-4 w-4", chevronClassName)} {...chevronProps} />
          ) : (
            <ChevronRight className={cn("h-4 w-4", chevronClassName)} {...chevronProps} />
          ),
      }}
      {...props}
    />
  )
}
Calendar.displayName = "Calendar"

export { Calendar }
